import { useRef, useEffect, useState } from 'react';

import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { DocVisibility } from '@/lib/api';

interface VisibilityIconProps {
  visibility: string;
  className?: string;
  disabled?: boolean;
}

export const VisibilityIcon: React.FC<VisibilityIconProps> = ({
  visibility,
  className,
  disabled,
}) => {
  const getIcon = () => {
    switch (visibility) {
      case 'public':
        return <PublicIcon aria-label="Public visibility" />;
      case 'friends':
        return <GroupsIcon aria-label="Friends-only visibility" />;
      case 'private':
        return <PersonIcon aria-label="Private visibility" />;
      default:
        return null;
    }
  };

  return (
    <Box
      component="span"
      className={className}
      sx={{ opacity: disabled ? 0.5 : 1 }}
      title={`Post visibility: ${visibility}${disabled ? ' (unavailable)' : ''}`}
      role="img"
      aria-label={`Post visibility: ${visibility}${disabled ? ' (unavailable)' : ''}`}
    >
      {getIcon()}
    </Box>
  );
};

interface VisibilitySelectorProps {
  visibility: DocVisibility;
  setVisibility: (visibility: DocVisibility) => void;
  inheritedVisibility?: DocVisibility;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  position?: 'above' | 'below';
}

export const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  visibility,
  setVisibility,
  inheritedVisibility,
  showDropdown,
  setShowDropdown,
  position,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setShowDropdown(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowDropdown(false);
  };

  const handleMenuItemClick = (option: DocVisibility) => {
    setVisibility(option);
    handleClose();
  };

  const getRestrictiveness = (vis: string) => {
    const restrictiveness = { public: 0, friends: 1, private: 2 };
    return restrictiveness[vis as keyof typeof restrictiveness];
  };

  return (
    <>
      <Button
        onClick={handleClick}
        size="small"
        sx={{
          textTransform: 'none',
          color: 'inherit',
          textDecoration: 'underline',
          p: 0,
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
        }}
      >
        <VisibilityIcon visibility={visibility} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={showDropdown && Boolean(anchorEl)}
        onClose={handleClose}
        disableEnforceFocus
        anchorOrigin={{
          vertical: position === 'below' ? 'bottom' : 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: position === 'below' ? 'top' : 'bottom',
          horizontal: 'left',
        }}
      >
        {['public', 'friends', 'private'].map((option) => {
          const isDisabled = inheritedVisibility
            ? getRestrictiveness(option) < getRestrictiveness(inheritedVisibility)
            : false;

          return (
            <MenuItem
              key={option}
              onClick={() => !isDisabled && handleMenuItemClick(option as DocVisibility)}
              disabled={isDisabled}
              selected={visibility === option}
            >
              <ListItemIcon>
                <VisibilityIcon visibility={option} disabled={isDisabled} />
              </ListItemIcon>
              <ListItemText>{option}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
